import { Lock } from "lucide-react";

import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { MultiSelect } from "../ui/multi-select";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";

// O que a campanha faz com o contato além de mandar a mensagem.
//
// O mesmo bloco serve aos dois momentos — assim que a mensagem sai e quando a
// pessoa responde —, porque as ações são as mesmas; muda só o que faz sentido
// em cada um. Encerrar o atendimento, por exemplo, não aparece na resposta:
// fechar bem na hora em que a pessoa escreveu esconderia a resposta da equipe.
// O que cada escopo de cargo deixa a pessoa enxergar. Serve para o aviso
// dizer o que está errado, e não só que algo está.
const rotuloDoEscopo = (escopo) =>
  ({
    own: "somente os próprios atendimentos",
    team: "os atendimentos da equipe dela",
    queues: "os atendimentos das filas dela",
    company: "todos os atendimentos da empresa",
  })[escopo] || "mais do que os próprios atendimentos";

const AcoesDaCampanhaFields = ({
	acoes,
	onChange,
	tags = [],
	queues = [],
	raias = [],
	atendentes = [],
	momento = "envio",
}) => {
	const valor = acoes || {};

	// Grava só o que foi escolhido: chave vazia vira ausência, senão o backend
	// receberia `{adicionarTags: []}` e a tela mostraria uma ação que não faz
	// nada.
	const mudar = (campo, novo) => {
		const proximo = { ...valor };

		if (
			novo === null ||
			novo === "" ||
			novo === false ||
			(Array.isArray(novo) && novo.length === 0)
		) {
			delete proximo[campo];
		} else {
			proximo[campo] = novo;
		}

		onChange(Object.keys(proximo).length ? proximo : {});
	};

	// Quem foi escolhido para receber o atendimento, e se o cargo dessa pessoa
	// realmente isola. São coisas diferentes: escolher o dono não esconde nada
	// de ninguém — quem esconde é o escopo do cargo.
	const escolhido = valor.atribuirAoAtendente
		? atendentes.find(
				(pessoa) => Number(pessoa.id) === Number(valor.atribuirAoAtendente),
			)
		: null;

	const isolado = escolhido?.role?.ticketScope === "own";

	const opcoesDeTag = tags.map((tag) => ({
		value: String(tag.id),
		label: tag.name,
		color: tag.color,
	}));

	return (
		<div className="space-y-4">
			<div className="space-y-1.5">
				<Label>Marcar com etiqueta</Label>
				<MultiSelect
					options={opcoesDeTag}
					value={(valor.adicionarTags || []).map(String)}
					onChange={(ids) =>
						mudar(
							"adicionarTags",
							ids.map((id) => Number(id)),
						)
					}
					placeholder="Nenhuma etiqueta"
				/>
				<p className="text-xs text-muted-foreground">
					{momento === "envio"
						? "Entra no atendimento de quem recebeu — dá para filtrar por ela depois."
						: "Separa quem respondeu de quem só recebeu."}
				</p>
			</div>

			{momento === "envio" && (
				<div className="space-y-1.5">
					<Label>Tirar etiqueta</Label>
					<MultiSelect
						options={opcoesDeTag}
						value={(valor.removerTags || []).map(String)}
						onChange={(ids) =>
							mudar(
								"removerTags",
								ids.map((id) => Number(id)),
							)
						}
						placeholder="Nenhuma etiqueta"
					/>
					<p className="text-xs text-muted-foreground">
						Útil para tirar a marca de “a contatar” de quem já foi contatado.
					</p>
				</div>
			)}

			{raias.length > 0 && (
				<div className="space-y-1.5">
					<Label>Abrir cartão no Kanban</Label>
					<Select
						value={
							valor.criarCartaoNaColuna ? String(valor.criarCartaoNaColuna) : "nao"
						}
						onValueChange={(v) =>
							mudar("criarCartaoNaColuna", v === "nao" ? null : Number(v))
						}
					>
						<SelectTrigger>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="nao">Não abrir cartão</SelectItem>
							{raias.map((raia) => (
								<SelectItem key={raia.id} value={String(raia.id)}>
									{raia.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<p className="text-xs text-muted-foreground">
						Um cartão por pessoa, com o nome dela e a campanha na descrição.
					</p>
				</div>
			)}

			<div className="space-y-1.5">
				<Label>Mandar o atendimento para uma fila</Label>
				<Select
					value={valor.moverParaFila ? String(valor.moverParaFila) : "nao"}
					onValueChange={(v) =>
						mudar("moverParaFila", v === "nao" ? null : Number(v))
					}
				>
					<SelectTrigger>
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="nao">Deixar como está</SelectItem>
						{queues.map((fila) => (
							<SelectItem key={fila.id} value={String(fila.id)}>
								{fila.name}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				<p className="text-xs text-muted-foreground">
					{momento === "envio"
						? "Quem receber já cai na fila certa antes de responder."
						: "A resposta chega no time que vai atender, em vez de ficar sem dono."}
				</p>
			</div>

			{atendentes.length > 0 && (
				<div
					className={
						momento === "resposta"
							? "space-y-1.5 rounded-lg border border-primary/40 bg-primary/5 p-3"
							: "space-y-1.5"
					}
				>
					<Label className="flex items-center gap-2">
						{momento === "resposta" && <Lock className="h-4 w-4 text-primary" />}
						{momento === "resposta"
							? "Atendimento exclusivo de um atendente"
							: "Entregar o atendimento a alguém"}
					</Label>

					{momento === "resposta" && (
						<p className="text-xs leading-relaxed text-muted-foreground">
							É assim que vários atendentes dividem o <b>mesmo número</b> sem um
							ver a conversa do outro: cada um cria a campanha da lista dele e
							escolhe a si mesmo aqui. Quem responder cai direto na mão dessa
							pessoa, sem passar pela fila de “aguardando”, que é compartilhada.
						</p>
					)}

					<Select
						value={
							valor.atribuirAoAtendente
								? String(valor.atribuirAoAtendente)
								: "nao"
						}
						onValueChange={(v) =>
							mudar("atribuirAoAtendente", v === "nao" ? null : Number(v))
						}
					>
						<SelectTrigger>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="nao">Ninguém em especial</SelectItem>
							{atendentes.map((pessoa) => (
								<SelectItem key={pessoa.id} value={String(pessoa.id)}>
									{pessoa.name}
									{pessoa.role?.name ? ` · ${pessoa.role.name}` : ""}
								</SelectItem>
							))}
						</SelectContent>
					</Select>

					{/* O aviso que evita a falha silenciosa: dá para escolher a pessoa,
					    achar que isolou, e o cargo dela deixar todo mundo ver a conversa
					    do mesmo jeito. Quem decide o isolamento é o cargo, não este
					    campo — então o campo precisa dizer isso. */}
					{escolhido && !isolado && (
						<p className="rounded-md bg-amber-500/10 px-2.5 py-2 text-xs leading-relaxed text-amber-700 dark:text-amber-400">
							O atendimento vai para <b>{escolhido.name}</b>, mas{" "}
							<b>não fica escondido dos outros</b>: o cargo
							{escolhido.role?.name ? ` “${escolhido.role.name}”` : ""} permite
							ver {rotuloDoEscopo(escolhido.role?.ticketScope)}. Para isolar de
							verdade, mude o escopo do cargo para “Somente os próprios
							atendimentos” em Configurações › Cargos e permissões.
						</p>
					)}

					{escolhido && isolado && (
						<p className="rounded-md bg-emerald-500/10 px-2.5 py-2 text-xs leading-relaxed text-emerald-700 dark:text-emerald-400">
							Isolado: o cargo de <b>{escolhido.name}</b> só enxerga os próprios
							atendimentos, então ninguém mais vê nem responde essa conversa.
						</p>
					)}

					{momento === "envio" && !escolhido && (
						<p className="text-xs leading-relaxed text-muted-foreground">
							A conversa nasce na mão dessa pessoa, sem passar pela fila.
						</p>
					)}
				</div>
			)}

			{momento === "envio" && (
				<div className="flex items-start justify-between gap-3">
					<div className="min-w-0">
						<Label htmlFor="encerrarAtendimento">
							Encerrar o atendimento depois de enviar
						</Label>
						<p className="text-xs leading-relaxed text-muted-foreground">
							A conversa sai da lista de quem está atendendo. Se a pessoa
							responder, ela volta sozinha.
						</p>
					</div>
					<Switch
						id="encerrarAtendimento"
						checked={Boolean(valor.encerrarAtendimento)}
						onCheckedChange={(v) => mudar("encerrarAtendimento", v)}
					/>
				</div>
			)}

			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0">
					<Label htmlFor={`webhook-${momento}`}>Avisar seus webhooks</Label>
					<p className="text-xs leading-relaxed text-muted-foreground">
						Dispara o evento{" "}
						<code className="text-[11px]">
							{momento === "envio" ? "campaign.sent" : "campaign.replied"}
						</code>{" "}
						para quem estiver escutando em Configurações › Webhooks.
					</p>
				</div>
				<Switch
					id={`webhook-${momento}`}
					checked={Boolean(valor.dispararWebhook)}
					onCheckedChange={(v) => mudar("dispararWebhook", v)}
				/>
			</div>
		</div>
	);
};

export default AcoesDaCampanhaFields;
